// SpeechSentencePractice.tsx
import React, {
  useState,
  useEffect,
} from "react";
import styles from "./SpeechSentencePractice.module.css";

// --- API Imports ---
import { fetchCourseOrderData } from "../../API/Course/CourseAPI";
import { 
  fetchExistingReconstructExercises as fetchReconstructExercises,
  createReconstructExercise,
  updateReconstructExercise,
  deleteReconstructExercise
} from "../../API/Lesson/SentenceTranslationAPI";
import { ReconstructExerciseDBRecord } from "../../Lesson/SentenceTranslation/SentenceTranslation";

// --- Language Constants ---
const AVAILABLE_LANGUAGES = [
  "English",
  "Spanish",
  "Italian",
  "French",
  "German",
  "Portuguese",
];
const LANG_CODE_MAP: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};
const PROFICIENCY_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const DIFFICULTIES = ["1", "2", "3"];
const DOMAINS = ["general", "construction", "medical", "business", "travel", "technical", "food"];

// --- Interface Definitions ---
interface CourseOrderItem {
  Identifier: string;
  Vocabulary_List: string;
}

interface FormData {
  sectionId: string;
  sentenceSource: string;
  sentenceTarget: string;
  targetLanguage: string;
  baseLanguage: string;
  domain: string;
  proficiencyLevel: string;
  difficulty: string;
}

// --- Component ---
const SpeechSentencePractice: React.FC = () => {
  // --- State Management ---
  const [selectedLanguage, setSelectedLanguage] = useState<string>("Spanish");
  const [courseOrder, setCourseOrder] = useState<CourseOrderItem[]>([]);
  const [exercises, setExercises] = useState<ReconstructExerciseDBRecord[]>([]);
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<ReconstructExerciseDBRecord | null>(null);
  const [message, setMessage] = useState<string>("");
  const [isCreating, setIsCreating] = useState<boolean>(false);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    sectionId: "",
    sentenceSource: "",
    sentenceTarget: "",
    targetLanguage: "",
    baseLanguage: "",
    domain: "general",
    proficiencyLevel: "A1",
    difficulty: "1",
  });

  // --- Derived Values ---
  const targetLangCode = LANG_CODE_MAP[selectedLanguage] || "ES";

  // --- Effects ---
  useEffect(() => {
    fetchCourseOrder();
  }, [targetLangCode]);

  useEffect(() => {
    if (selectedSection) {
      fetchExercisesForSection();
    }
  }, [selectedSection]);

  // --- API Functions ---
  const fetchCourseOrder = async () => {
    try {
      const data = await fetchCourseOrderData(targetLangCode);
      
      // Sort the sections by their Identifier (which contains the order number)
      const sortedData = data.sort((a, b) => {
        const aOrder = parseInt(a.Identifier) || 0;
        const bOrder = parseInt(b.Identifier) || 0;
        return aOrder - bOrder;
      });
      
      setCourseOrder(sortedData);
      
      // Auto-select first section if available
      if (sortedData.length > 0 && !selectedSection) {
        setSelectedSection(sortedData[0].Vocabulary_List);
      }
    } catch (error) {
      console.error("Error fetching course order:", error);
      setMessage("Error fetching course sections");
    }
  };

  const fetchExercisesForSection = async () => {
    if (!selectedSection) return;
    
    setLoading(true);
    try {
      const data = await fetchReconstructExercises(selectedSection);
      setExercises(data);
      setMessage(`Loaded ${data.length} sentences for speech practice`);
    } catch (error) {
      console.error("Error fetching exercises:", error);
      setMessage("Error fetching sentences");
      setExercises([]);
    } finally {
      setLoading(false);
    }
  };

  // --- Form Handlers ---
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      sectionId: selectedSection,
      sentenceSource: "",
      sentenceTarget: "",
      targetLanguage: targetLangCode,
      baseLanguage: "EN",
      domain: "general",
      proficiencyLevel: "A1",
      difficulty: "1",
    });
    setEditingItem(null);
    setIsCreating(false);
  };

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      sectionId: selectedSection,
      sentenceSource: "",
      sentenceTarget: "",
      targetLanguage: targetLangCode,
      baseLanguage: "EN",
      domain: "general",
      proficiencyLevel: "A1",
      difficulty: "1",
    });
  };

  const handleEdit = (item: ReconstructExerciseDBRecord) => {
    setEditingItem(item);
    setIsCreating(false);
    setFormData({
      sectionId: item.sectionId,
      sentenceSource: item.sentenceSource || "",
      sentenceTarget: item.sentenceTarget || "",
      targetLanguage: item.targetLanguage,
      baseLanguage: item.baseLanguage,
      domain: item.domain,
      proficiencyLevel: item.proficiencyLevel,
      difficulty: item.difficulty,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.sentenceTarget.trim()) {
      setMessage("Target sentence is required");
      return;
    }

    setLoading(true);
    try {
      if (editingItem) {
        // Update existing item
        await updateReconstructExercise(editingItem.sectionId, editingItem.exerciseId, {
          sentenceSource: formData.sentenceSource,
          sentenceTarget: formData.sentenceTarget,
          targetLanguage: formData.targetLanguage,
          baseLanguage: formData.baseLanguage,
          domain: formData.domain,
          proficiencyLevel: formData.proficiencyLevel,
          difficulty: formData.difficulty,
          // Keep existing word-related fields empty since we only care about sentences
          wordId: editingItem.wordId || "",
          wordsOrderedBase: editingItem.wordsOrderedBase || "[]",
          wordsOrderedTarget: editingItem.wordsOrderedTarget || "[]",
          distractorsBase: editingItem.distractorsBase || "[]",
          distractorsTarget: editingItem.distractorsTarget || "[]",
          selectedWordId: editingItem.selectedWordId || "",
        });
        setMessage("Speech sentence updated successfully");
      } else {
        // Create new item
        await createReconstructExercise({
          sectionId: formData.sectionId,
          exerciseId: "", // Will be generated by the backend
          sentenceSource: formData.sentenceSource,
          sentenceTarget: formData.sentenceTarget,
          targetLanguage: formData.targetLanguage,
          baseLanguage: formData.baseLanguage,
          domain: formData.domain,
          proficiencyLevel: formData.proficiencyLevel,
          difficulty: formData.difficulty,
          // Set minimal values for word-related fields since we only care about sentences
          wordId: "",
          wordsOrderedBase: "[]",
          wordsOrderedTarget: "[]",
          distractorsBase: "[]",
          distractorsTarget: "[]",
          createdAt: new Date().toISOString(),
          selectedWordId: "",
        });
        setMessage("Speech sentence created successfully");
      }
      
      resetForm();
      fetchExercisesForSection();
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: ReconstructExerciseDBRecord) => {
    if (!window.confirm(`Are you sure you want to delete this speech sentence?`)) {
      return;
    }

    setLoading(true);
    try {
      await deleteReconstructExercise(item.sectionId, item.exerciseId);
      setMessage("Speech sentence deleted successfully");
      fetchExercisesForSection();
    } catch (error: any) {
      setMessage(`Error deleting: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // --- Render ---
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Speech Sentence Practice Management</h1>
        <p>Manage sentences for speech repetition practice (uses ReconstructExercises data)</p>
      </div>

      {/* Language and Section Selection */}
      <div className={styles.controls}>
        <div className={styles.controlGroup}>
          <label htmlFor="language">Target Language:</label>
          <select
            id="language"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className={styles.select}
          >
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.controlGroup}>
          <label htmlFor="section">Section:</label>
          <select
            id="section"
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className={styles.select}
          >
            <option value="">Select a section</option>
            {courseOrder.map((section) => (
              <option key={section.Identifier} value={section.Vocabulary_List}>
                {section.Identifier} - {section.Vocabulary_List}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleCreate}
          disabled={!selectedSection || loading}
          className={styles.createButton}
        >
          Create New Speech Sentence
        </button>
      </div>

      {/* Status Message */}
      {message && (
        <div className={styles.message}>
          {message}
        </div>
      )}

      {/* Form */}
      {(isCreating || editingItem) && (
        <div className={styles.formContainer}>
          <h3>{editingItem ? "Edit Speech Sentence" : "Create New Speech Sentence"}</h3>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="sentenceSource">Source Sentence (Optional):</label>
                <textarea
                  id="sentenceSource"
                  name="sentenceSource"
                  value={formData.sentenceSource}
                  onChange={handleInputChange}
                  placeholder="Enter the source sentence (base language)..."
                  rows={2}
                  className={styles.textarea}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="sentenceTarget">Target Sentence *:</label>
                <textarea
                  id="sentenceTarget"
                  name="sentenceTarget"
                  value={formData.sentenceTarget}
                  onChange={handleInputChange}
                  placeholder="Enter the sentence to be repeated..."
                  required
                  rows={2}
                  className={styles.textarea}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="targetLanguage">Target Language:</label>
                <select
                  id="targetLanguage"
                  name="targetLanguage"
                  value={formData.targetLanguage}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  {Object.entries(LANG_CODE_MAP).map(([name, code]) => (
                    <option key={code} value={code}>
                      {name} ({code})
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="baseLanguage">Base Language:</label>
                <select
                  id="baseLanguage"
                  name="baseLanguage"
                  value={formData.baseLanguage}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  {Object.entries(LANG_CODE_MAP).map(([name, code]) => (
                    <option key={code} value={code}>
                      {name} ({code})
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="domain">Domain:</label>
                <select
                  id="domain"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  {DOMAINS.map((domain) => (
                    <option key={domain} value={domain}>
                      {domain}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="proficiencyLevel">Proficiency Level:</label>
                <select
                  id="proficiencyLevel"
                  name="proficiencyLevel"
                  value={formData.proficiencyLevel}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  {PROFICIENCY_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="difficulty">Difficulty:</label>
                <select
                  id="difficulty"
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  className={styles.select}
                >
                  {DIFFICULTIES.map((difficulty) => (
                    <option key={difficulty} value={difficulty}>
                      {difficulty}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.formActions}>
              <button
                type="submit"
                disabled={loading}
                className={styles.submitButton}
              >
                {loading ? "Saving..." : (editingItem ? "Update" : "Create")}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sentences List */}
      {selectedSection && (
        <div className={styles.listContainer}>
          <h3>Speech Sentences for {selectedSection}</h3>
          <p className={styles.note}>
            <strong>Note:</strong> This interface uses ReconstructExercises data. 
            For speech practice, only the sentence text is relevant - word lists and distractors are ignored.
          </p>
          {loading ? (
            <div className={styles.loading}>Loading...</div>
          ) : exercises.length === 0 ? (
            <div className={styles.noData}>No sentences found for this section.</div>
          ) : (
            <div className={styles.itemsList}>
              {exercises.map((item) => (
                <div key={item.exerciseId} className={styles.itemCard}>
                  <div className={styles.itemHeader}>
                    <h4>{item.sentenceTarget || "No target sentence"}</h4>
                    <div className={styles.itemActions}>
                      <button
                        onClick={() => handleEdit(item)}
                        className={styles.editButton}
                        disabled={loading}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className={styles.deleteButton}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className={styles.itemDetails}>
                    {item.sentenceSource && (
                      <span><strong>Source:</strong> {item.sentenceSource}</span>
                    )}
                    <span><strong>Languages:</strong> {item.baseLanguage} → {item.targetLanguage}</span>
                    <span><strong>Domain:</strong> {item.domain}</span>
                    <span><strong>Level:</strong> {item.proficiencyLevel}</span>
                    <span><strong>Difficulty:</strong> {item.difficulty}</span>
                    <span><strong>Created:</strong> {new Date(item.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpeechSentencePractice;